import type { ArticlePrice } from "@/types/camping";

export const UNIT_LABELS: Record<string, string> = {
  night: "Nacht",
  piece: "Stück",
  person_night: "Person × Nacht",
  kwh: "kWh",
  week: "Woche",
  month: "Monat",
  year: "Jahr",
};

/** Referenz-Nächte je Zeiteinheit (Grundlage für Teilberechnungen). */
export const UNIT_NIGHTS: Record<string, number> = {
  night: 1,
  week: 7,
  month: 30,
  year: 365,
};

export function unitNights(unit: string | null | undefined): number | null {
  if (!unit) return null;
  return UNIT_NIGHTS[unit] ?? null;
}

export function isPeriodUnit(unit: string | null | undefined): boolean {
  return unitNights(unit) != null;
}

/** Anteilige Menge aus Nächten, z. B. 100 Nächte bei Jahrespreis → 100/365. */
export function quantityFromNights(nights: number, unit: string): number | null {
  const nightsPerUnit = unitNights(unit);
  if (nightsPerUnit == null || nights < 1) return null;
  return Math.round((nights / nightsPerUnit) * 10000) / 10000;
}

export const ELECTRICITY_BILLING_MODE_LABELS: Record<string, string> = {
  flat_per_night: "Strompauschale pro Nacht",
  per_kwh: "Abrechnung pro kWh",
  flat_with_included_kwh: "Pauschale mit inkludierten kWh",
};

export const TAX_RATE_PRESETS = [
  { value: "0", label: "0 %", description: "steuerfrei" },
  { value: "7", label: "7 %", description: "ermäßigt" },
  { value: "19", label: "19 %", description: "Regelsteuersatz" },
] as const;

export const DEFAULT_TAX_RATE = "7";

export function isPresetTaxRate(value: string): boolean {
  return TAX_RATE_PRESETS.some((preset) => preset.value === value);
}

/** True, wenn asOf im Fenster von gültig-ab bis einschließlich Ablaufdatum liegt. */
export function priceCoversDate(price: ArticlePrice, asOf: string): boolean {
  if (price.valid_from > asOf) return false;
  if (price.valid_until && price.valid_until < asOf) return false;
  return true;
}

/** Gültiger Preis: höchstes valid_from, dessen Fenster asOf noch enthält. */
export function effectiveArticlePrice(
  prices: ArticlePrice[],
  asOf: string,
): ArticlePrice | null {
  const applicable = prices.filter((price) => priceCoversDate(price, asOf));
  if (applicable.length === 0) return null;
  return applicable.reduce((best, price) =>
    price.valid_from > best.valid_from ? price : best,
  );
}
